import React from "react";

import { Box, Grid } from "@mui/material";
import { useUserDataContext } from "context/UserDataContext";
import { useAPIDashboardStrategiesCharts } from "hooks/useAPI";
import { useTranslation } from "react-i18next";

import BaseCurrencyAmountChart from "./BaseCurrencyAmountChart";
import InvestmentChart from "./InvestmentChart";
import PriceChart from "./PriceChart";
import Skeleton from "./Skeleton";
import ChartWrapper from "./Wrapper";

interface IProps {
  currencyPairs: string[];
}

export const SMALL_CHART_MAX_SAMPLES = 200;
export const SUMMARY_CHART_HEIGHT = 350;

const SummaryCharts = ({ currencyPairs }: IProps) => {
  const { t } = useTranslation<string>();
  const { summariesSettings } = useUserDataContext();
  const { data } = useAPIDashboardStrategiesCharts(true, { ...summariesSettings, fullscreen: false });

  if (!data) return <Skeleton currencyPairs={currencyPairs} />;

  const currencyEntries = Object.entries(data.chartsByCurrency);
  const singleCurrency = currencyEntries.length === 1;

  return (
    <>
      <Grid container spacing={2}>
        {currencyEntries.map(([currency, currencyData]) => (
          <Grid item xs={12} lg={singleCurrency ? 12 : 6} key={`charts-by-currency-${currency}`}>
            <ChartWrapper title={t("summaries.charts.amountChart", { currency })} noPaddingBottom>
              <BaseCurrencyAmountChart chartData={currencyData.baseCurrencyAmountChart} currency={currency} />
            </ChartWrapper>
          </Grid>
        ))}
      </Grid>
      <Box mt={2}>
        <Grid container spacing={2}>
          {Object.entries(data.chartsByCurrencyPair).map(([pair, pairData]) => (
            <React.Fragment key={pair}>
              <Grid item xs={12} lg={6}>
                <ChartWrapper title={t("summaries.charts.investmentChart", { currency: pair })}>
                  <InvestmentChart chartData={pairData.investmentChart} currencyPair={pair} />
                </ChartWrapper>
              </Grid>
              <Grid item xs={12} lg={6}>
                <ChartWrapper title={t("summaries.charts.priceChart", { currency: pair })}>
                  <PriceChart chartData={pairData.priceChart} currencyPair={pair} />
                </ChartWrapper>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Box>
    </>
  );
};

export default SummaryCharts;
